import { useQueryClient } from '@tanstack/react-query';
import {
  ConvertInsightResponseSchema,
  InsightListResponseSchema,
  InsightRecommendationSchema,
  InsightSettingsSchema,
  RunInsightsResponseSchema,
} from '@growhero/shared';
import { api } from '@/lib/api';
import { tenantKey, useApiMutation, useApiQuery } from '@/lib/query';

export type InsightView = 'open' | 'saved' | 'history';

export function useInsights(view: InsightView, opts: { enabled?: boolean; limit?: number } = {}) {
  return useApiQuery(['insights', view, opts.limit ?? 20], '/insights', {
    schema: InsightListResponseSchema,
    query: { view, limit: opts.limit ?? 20 },
    enabled: opts.enabled,
    staleTime: 30_000,
  });
}

function useInvalidate() {
  const qc = useQueryClient();
  return () => qc.invalidateQueries({ queryKey: tenantKey('insights') });
}

export function useDecideInsight() {
  const invalidate = useInvalidate();
  return useApiMutation(
    (v: { id: string; action: 'dismiss' | 'save' | 'restore' }) =>
      api.post(`/insights/${v.id}/${v.action}`, {}, { schema: InsightRecommendationSchema }),
    { onSuccess: () => void invalidate() },
  );
}

export function useConvertInsight() {
  const invalidate = useInvalidate();
  const qc = useQueryClient();
  return useApiMutation(
    (id: string) =>
      api.post(`/insights/${id}/convert`, {}, { schema: ConvertInsightResponseSchema }),
    {
      onSuccess: () => {
        void invalidate();
        void qc.invalidateQueries({ queryKey: tenantKey('campaigns') });
      },
    },
  );
}

export function useRunInsights() {
  const invalidate = useInvalidate();
  const qc = useQueryClient();
  return useApiMutation(
    () => api.post('/insights/run', {}, { schema: RunInsightsResponseSchema }),
    {
      onSuccess: () => {
        void invalidate();
        void qc.invalidateQueries({ queryKey: ['tenant-current'] });
      },
    },
  );
}

export function useUpdateInsightSettings() {
  const invalidate = useInvalidate();
  return useApiMutation(
    (optOut: boolean) =>
      api.put('/insights/settings', { optOut }, { schema: InsightSettingsSchema }),
    { onSuccess: () => void invalidate() },
  );
}
